<template>
  <div class="sitemap-wrap">
    <!-- Header -->
    <div class="sitemap-header">
      <div class="sitemap-header-inner">
        <div class="sitemap-title-row">
          <h1 class="sitemap-title">Productoverzicht</h1>
          <div class="sitemap-controls">
            <button class="ctrl-btn" @click="expandAll">Alles uitklappen</button>
            <span class="ctrl-divider">·</span>
            <button class="ctrl-btn" @click="collapseAll">Alles inklappen</button>
          </div>
        </div>

        <!-- Search -->
        <div class="search-wrap">
          <span class="search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </span>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Zoek producten, artikelcodes..."
            autocomplete="off"
          />
          <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">✕</button>
        </div>

        <!-- Stats -->
        <div v-if="!loading && catalog" class="stats-bar">
          <span>{{ stats.categories }} categorieën</span>
          <span class="dot">·</span>
          <span>{{ stats.toepassingen }} toepassingen</span>
          <span class="dot">·</span>
          <span>{{ stats.types }} types</span>
          <span class="dot">·</span>
          <span><strong>{{ stats.articles }}</strong> artikelen</span>
          <template v-if="searchQuery && filteredStats !== null">
            <span class="dot">·</span>
            <span class="match-count">{{ filteredStats }} resultaten</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-msg">
      <div class="spinner"></div>
      <span>Catalogus laden...</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="state-msg error">
      <span>⚠ Fout bij laden: {{ error }}</span>
    </div>

    <!-- No results -->
    <div v-else-if="searchQuery && filteredCatalog.length === 0" class="state-msg">
      <span>Geen resultaten gevonden voor "<strong>{{ searchQuery }}</strong>"</span>
    </div>

    <!-- Tree -->
    <div v-else class="sitemap-tree">
      <div
        v-for="cat in filteredCatalog"
        :key="cat.maingroup_code"
        class="level1-block"
        :style="{ '--cat-color': categoryColor(cat.maingroup_code) }"
      >
        <!-- Level 1: Category -->
        <div class="level1-header" @click="toggle('cat:' + cat.maingroup_code)">
          <span class="chevron" :class="{ open: isOpen('cat:' + cat.maingroup_code) }">▶</span>
          <span class="level1-label">{{ cat.label }}</span>
          <span class="count-badge">{{ cat.articleCount }}</span>
        </div>

        <!-- Level 2: Toepassingen -->
        <div v-if="isOpen('cat:' + cat.maingroup_code)" class="level2-wrap">
          <div
            v-for="toep in cat.toepassingen"
            :key="toep.toepassing"
            class="level2-block"
          >
            <div class="level2-header" @click="toggle('toep:' + cat.maingroup_code + '|' + toep.toepassing)">
              <span class="chevron" :class="{ open: isOpen('toep:' + cat.maingroup_code + '|' + toep.toepassing) }">▶</span>
              <span class="level2-label">{{ toep.label }}</span>
              <span class="count-badge">{{ toep.articleCount }}</span>
            </div>

            <!-- Level 3: Types -->
            <div v-if="isOpen('toep:' + cat.maingroup_code + '|' + toep.toepassing)" class="level3-wrap">
              <div
                v-for="typ in toep.types"
                :key="typ.type"
                class="level3-block"
              >
                <div class="level3-header" @click="toggle('type:' + cat.maingroup_code + '|' + toep.toepassing + '|' + typ.type)">
                  <span class="chevron" :class="{ open: isOpen('type:' + cat.maingroup_code + '|' + toep.toepassing + '|' + typ.type) }">▶</span>
                  <span class="level3-label">{{ typ.label }}</span>
                  <span class="count-badge">{{ typ.articleCount }}</span>
                </div>

                <!-- Level 4: Subgroups -->
                <div v-if="isOpen('type:' + cat.maingroup_code + '|' + toep.toepassing + '|' + typ.type)" class="level4-wrap">
                  <div
                    v-for="sub in typ.subgroups"
                    :key="sub.code"
                    class="level4-block"
                  >
                    <div class="level4-header" @click="toggle('sub:' + cat.maingroup_code + '|' + toep.toepassing + '|' + typ.type + '|' + sub.code)">
                      <span class="chevron" :class="{ open: isOpen('sub:' + cat.maingroup_code + '|' + toep.toepassing + '|' + typ.type + '|' + sub.code) }">▶</span>
                      <span class="level4-label">{{ sub.label }}</span>
                      <code class="sub-code">{{ sub.code }}</code>
                      <span class="count-badge">{{ sub.articleCount }}</span>
                    </div>

                    <!-- Articles -->
                    <div v-if="isOpen('sub:' + cat.maingroup_code + '|' + toep.toepassing + '|' + typ.type + '|' + sub.code)" class="articles-wrap">
                      <div
                        v-for="article in sub.articles"
                        :key="article.article_id"
                        class="article-row"
                        :class="{ highlighted: searchQuery && articleMatches(article, sub, typ, toep, cat) }"
                      >
                        <code class="article-code">{{ article.article_code }}</code>
                        <a
                          v-if="productUrl(article.article_code)"
                          :href="productUrl(article.article_code)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="article-link"
                        >{{ article.description }}</a>
                        <span v-else class="article-text">{{ article.description }}</span>
                        <span v-if="article.weight_kg" class="article-meta">{{ article.weight_kg }} kg</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from "@/services/AxiosInstance";
import UTDService from "@/services/UTDService";

export default {
  name: "SitemapView",
  data() {
    return {
      catalog: null,
      urlMap: {},
      searchQuery: "",
      expanded: {},
      loading: true,
      error: null,
    };
  },
  computed: {
    stats() {
      if (!this.catalog) return { categories: 0, toepassingen: 0, types: 0, articles: 0 };
      let toepassingen = 0, types = 0, articles = 0;
      for (const cat of this.catalog.categories) {
        toepassingen += cat.toepassingen.length;
        for (const toep of cat.toepassingen) {
          types += toep.types.length;
          articles += toep.articleCount;
        }
      }
      return {
        categories: this.catalog.categories.length,
        toepassingen,
        types,
        articles,
      };
    },

    filteredCatalog() {
      if (!this.catalog) return [];
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.catalog.categories;

      const result = [];
      for (const cat of this.catalog.categories) {
        const catMatch = cat.label.toLowerCase().includes(q);
        const filteredToeps = [];

        for (const toep of cat.toepassingen) {
          const toepMatch = catMatch || toep.label.toLowerCase().includes(q);
          const filteredTypes = [];

          for (const typ of toep.types) {
            const typeMatch = toepMatch || typ.label.toLowerCase().includes(q);
            const filteredSubs = [];

            for (const sub of typ.subgroups) {
              const subMatch = typeMatch || sub.label.toLowerCase().includes(q) || sub.code.toLowerCase().includes(q);
              const matchingArticles = sub.articles.filter(a =>
                subMatch ||
                a.description.toLowerCase().includes(q) ||
                a.article_code.toLowerCase().includes(q)
              );
              if (matchingArticles.length > 0) {
                filteredSubs.push({ ...sub, articles: matchingArticles, articleCount: matchingArticles.length });
              }
            }

            if (filteredSubs.length > 0) {
              filteredTypes.push({ ...typ, subgroups: filteredSubs, articleCount: filteredSubs.reduce((s, x) => s + x.articleCount, 0) });
            }
          }

          if (filteredTypes.length > 0) {
            filteredToeps.push({ ...toep, types: filteredTypes, articleCount: filteredTypes.reduce((s, x) => s + x.articleCount, 0) });
          }
        }

        if (filteredToeps.length > 0) {
          result.push({ ...cat, toepassingen: filteredToeps, articleCount: filteredToeps.reduce((s, x) => s + x.articleCount, 0) });
        }
      }
      return result;
    },

    filteredStats() {
      if (!this.searchQuery) return null;
      return this.filteredCatalog.reduce((sum, cat) => sum + cat.articleCount, 0);
    },
  },
  watch: {
    searchQuery(q) {
      if (q.trim()) {
        this.autoExpand();
      }
    },
  },
  async created() {
    await Promise.all([this.fetchCatalog(), this.fetchUrlMap()]);
    this.loading = false;
  },
  methods: {
    async fetchCatalog() {
      const lang = (this.shopSettings?.lang || "nl").toUpperCase();
      const catalogUrl = `https://andersbeton-catalog.vercel.app/api/catalog?lang=${lang}`;
      try {
        const { data } = await axiosInstance.post("/blogger/proxy", {
          url: catalogUrl,
          method: "GET",
        }, { responseType: "arraybuffer" });
        const text = new TextDecoder().decode(data);
        this.catalog = JSON.parse(text);
      } catch (e) {
        this.error = e.message || "Onbekende fout";
      }
    },

    async fetchUrlMap() {
      const siteId = this.shopSettings?.siteId;
      if (!siteId) return;
      try {
        const products = await UTDService.getProducts("Producten", "nl", siteId);
        const map = {};
        for (const p of products) {
          const code = p.locationPageMeta?.articleCode;
          if (code && p.bloggerPageUrl) {
            map[code.toLowerCase()] = p.bloggerPageUrl;
          }
        }
        this.urlMap = map;
      } catch (e) {
        // Non-fatal: tree still renders without links
        console.warn("Could not load URL map:", e.message);
      }
    },

    productUrl(articleCode) {
      if (!articleCode) return null;
      const path = this.urlMap[articleCode.toLowerCase()];
      if (!path) return null;
      const base = (this.shopSettings?.baseUrl || "").replace(/\/$/, "");
      return base + path;
    },

    toggle(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },

    isOpen(key) {
      return !!this.expanded[key];
    },

    expandAll() {
      if (!this.catalog) return;
      const next = {};
      for (const cat of this.filteredCatalog) {
        next["cat:" + cat.maingroup_code] = true;
        for (const toep of cat.toepassingen) {
          next["toep:" + cat.maingroup_code + "|" + toep.toepassing] = true;
          for (const typ of toep.types) {
            next["type:" + cat.maingroup_code + "|" + toep.toepassing + "|" + typ.type] = true;
            for (const sub of typ.subgroups) {
              next["sub:" + cat.maingroup_code + "|" + toep.toepassing + "|" + typ.type + "|" + sub.code] = true;
            }
          }
        }
      }
      this.expanded = next;
    },

    collapseAll() {
      this.expanded = {};
    },

    autoExpand() {
      const next = { ...this.expanded };
      for (const cat of this.filteredCatalog) {
        next["cat:" + cat.maingroup_code] = true;
        for (const toep of cat.toepassingen) {
          next["toep:" + cat.maingroup_code + "|" + toep.toepassing] = true;
          for (const typ of toep.types) {
            next["type:" + cat.maingroup_code + "|" + toep.toepassing + "|" + typ.type] = true;
            for (const sub of typ.subgroups) {
              next["sub:" + cat.maingroup_code + "|" + toep.toepassing + "|" + typ.type + "|" + sub.code] = true;
            }
          }
        }
      }
      this.expanded = next;
    },

    categoryColor(code) {
      const map = {
        AGRO: "#38a933",
        INFRA: "#f59c00",
        MODULO: "#d34752",
      };
      return map[code] || "#009DE2";
    },

    articleMatches(article, sub, typ, toep, cat) {
      const q = this.searchQuery.trim().toLowerCase();
      return (
        article.description.toLowerCase().includes(q) ||
        article.article_code.toLowerCase().includes(q) ||
        sub.label.toLowerCase().includes(q) ||
        sub.code.toLowerCase().includes(q) ||
        typ.label.toLowerCase().includes(q) ||
        toep.label.toLowerCase().includes(q) ||
        cat.label.toLowerCase().includes(q)
      );
    },
  },
};
</script>

<style scoped>
/* ---- Wrap ---- */
.sitemap-wrap {
  min-height: 100vh;
  background: #f8f9fa;
  font-family: var(--body-font, 'Jost', sans-serif);
}

/* ---- Header ---- */
.sitemap-header {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.sitemap-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 24px 14px;
}
.sitemap-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.sitemap-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  font-family: var(--title-font, 'Lexend', sans-serif);
}
.sitemap-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ctrl-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #009DE2;
  font-size: 13px;
  padding: 4px 6px;
  border-radius: 4px;
  transition: background .15s;
}
.ctrl-btn:hover { background: #f0f9ff; }
.ctrl-divider { color: #cbd5e1; font-size: 13px; }

/* ---- Search ---- */
.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  display: flex;
  align-items: center;
}
.search-input {
  width: 100%;
  padding: 10px 36px 10px 38px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  transition: border-color .2s;
  background: #f9fafb;
  color: #111;
}
.search-input:focus {
  border-color: #009DE2;
  background: #fff;
}
.search-clear {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 14px;
  padding: 4px;
  border-radius: 50%;
}
.search-clear:hover { color: #374151; background: #f3f4f6; }

/* ---- Stats ---- */
.stats-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
  margin-top: 10px;
  flex-wrap: wrap;
}
.stats-bar strong { color: #111; }
.stats-bar .dot { color: #d1d5db; }
.match-count { color: #009DE2; font-weight: 600; }

/* ---- State messages ---- */
.state-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  font-size: 15px;
  color: #6b7280;
}
.state-msg.error { color: #dc2626; }
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #009DE2;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ---- Tree ---- */
.sitemap-tree {
  max-width: 1200px;
  margin: 24px auto;
  padding: 0 24px 60px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ---- Level 1 ---- */
.level1-block {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}
.level1-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  background: #fafafa;
  transition: background .15s;
}
.level1-header:hover { background: #f3f4f6; }
.level1-label {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #1a1a2e;
  flex: 1;
}

/* ---- Level 2 ---- */
.level2-wrap { padding: 0 0 8px 0; }
.level2-block { border-top: 1px solid #f0f0f0; }
.level2-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px 10px 32px;
  cursor: pointer;
  user-select: none;
  transition: background .15s;
}
.level2-header:hover { background: #f9fafb; }
.level2-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  flex: 1;
  letter-spacing: .02em;
}

/* ---- Level 3 ---- */
.level3-wrap { padding: 0; }
.level3-block { border-top: 1px solid #f5f5f5; }
.level3-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 18px 8px 52px;
  cursor: pointer;
  user-select: none;
  transition: background .15s;
}
.level3-header:hover { background: #f9fafb; }
.level3-label {
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  flex: 1;
  letter-spacing: .02em;
}

/* ---- Level 4 ---- */
.level4-wrap { padding: 0; }
.level4-block { border-top: 1px solid #f5f5f5; }
.level4-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 18px 7px 72px;
  cursor: pointer;
  user-select: none;
  transition: background .15s;
}
.level4-header:hover { background: #f9fafb; }
.level4-label {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  flex: 1;
}
.sub-code {
  font-size: 11px;
  background: #f3f4f6;
  color: #9ca3af;
  border-radius: 4px;
  padding: 1px 5px;
  font-family: 'Courier New', monospace;
}

/* ---- Articles ---- */
.articles-wrap {
  padding: 4px 18px 8px 92px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.article-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 5px;
  transition: background .15s;
}
.article-row:hover { background: #f9fafb; }
.article-row.highlighted { background: #fef9c3; }
.article-code {
  font-size: 11px;
  font-family: 'Courier New', monospace;
  background: #f1f5f9;
  color: #64748b;
  border-radius: 4px;
  padding: 1px 6px;
  white-space: nowrap;
  flex-shrink: 0;
}
.article-link {
  font-size: 13px;
  color: #009DE2;
  text-decoration: none;
  flex: 1;
  transition: color .15s;
}
.article-link:hover { color: #0077aa; text-decoration: underline; }
.article-text {
  font-size: 13px;
  color: #374151;
  flex: 1;
}
.article-meta {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
}

/* ---- Chevron ---- */
.chevron {
  font-size: 9px;
  color: #9ca3af;
  transition: transform .2s;
  display: inline-block;
  flex-shrink: 0;
}
.chevron.open { transform: rotate(90deg); }

/* ---- Count badge ---- */
.count-badge {
  font-size: 11px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 10px;
  padding: 1px 7px;
  font-weight: 500;
  white-space: nowrap;
}

/* ---- Responsive ---- */
@media (max-width: 640px) {
  .sitemap-header-inner { padding: 14px 16px 10px; }
  .sitemap-title { font-size: 18px; }
  .sitemap-title-row { flex-direction: column; align-items: flex-start; gap: 6px; }
  .sitemap-tree { padding: 0 12px 40px; margin-top: 16px; }
  .level4-header { padding-left: 52px; }
  .articles-wrap { padding-left: 60px; }
}
</style>
